import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, X } from "lucide-react";
import { MESSAGES, NAV_LINKS, scrollToId, waLink } from "@/data/content";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color] duration-500 ${
          scrolled
            ? "border-b border-white/10 bg-navy/85 backdrop-blur-md"
            : "border-b border-white/5 bg-navy/40 backdrop-blur-sm"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-10">
          <a
            href="#top"
            onClick={(e) => go(e, "#top")}
            data-testid="header-brand-link"
            className="leading-none"
          >
            <span className="font-display text-xl md:text-2xl font-bold tracking-[0.18em] text-cream">
              SHIYADN
            </span>
            <span className="mt-1 block font-head text-[11px] font-semibold text-copper">
              شيادن الأصول العقارية
            </span>
          </a>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="header-nav">
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={l.id}
                onClick={(e) => go(e, l.id)}
                data-testid={`nav-link-${l.id.slice(1)}`}
                className="font-body text-sm text-cream/80 transition-colors duration-300 hover:text-copper"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={waLink(MESSAGES.general)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-button"
              className="hidden items-center gap-2 rounded-full bg-copper px-5 py-2.5 font-head text-sm font-bold text-navy transition-transform duration-300 hover:scale-105 sm:inline-flex"
            >
              <MessageCircle className="h-4 w-4" />
              تواصل عبر واتساب
            </a>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open-button"
              aria-label="فتح القائمة"
              className="text-cream lg:hidden"
            >
              <Menu className="h-7 w-7" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-50 flex flex-col bg-navy/97 backdrop-blur-xl"
          >
            <div className="flex h-[72px] items-center justify-between px-5">
              <span className="font-display text-xl font-bold tracking-[0.18em] text-cream">
                SHIYADN
              </span>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close-button"
                aria-label="إغلاق القائمة"
                className="text-cream"
              >
                <X className="h-7 w-7" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.id}
                  onClick={(e) => go(e, l.id)}
                  data-testid={`mobile-nav-link-${l.id.slice(1)}`}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5 }}
                  className="border-b border-white/10 py-5 font-head text-3xl font-bold text-cream transition-colors duration-300 hover:text-copper"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href={waLink(MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-menu-whatsapp-button"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="mt-10 inline-flex items-center justify-center gap-3 rounded-full bg-copper py-4 font-head text-lg font-bold text-navy"
              >
                <MessageCircle className="h-5 w-5" />
                تواصل عبر واتساب
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
